// /help — Internal operator guide.
//
// Authenticated-only. Lives inside the <AppShell> tree so it is already
// guarded by <RequireAuth> via App.js. This is the long-form
// operator-facing documentation — distinct from the redacted public
// marketing docs at /docs/*.
//
// Layout: sticky TOC (with client-side search filter) + long-form
// right-hand column. Print-friendly: when printed, the TOC is hidden
// and each section starts on a new page.
//
// Guardrails:
//   * Nothing here references preset ids, collection names, or API
//     routes that the operator shouldn't already know — but since this
//     is behind auth, we are allowed to be concrete.
//   * No public exposure: not in sitemap, robots, seoPages, or
//     PublicLayout.

import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
    BookOpen,
    Check,
    Crosshair,
    FileText,
    FlaskConical,
    Keyboard,
    LayoutGrid,
    LifeBuoy,
    LineChart,
    Lock,
    Map as MapIcon,
    Mountain,
    PlayCircle,
    Printer,
    Radar,
    Rocket,
    Search,
    ShieldCheck,
    Target,
    UserCog,
    Wind,
} from "lucide-react";
import { useAuth } from "@/auth/AuthContext";

// ─────────────────────────────────────────────────────────────────────
// Lightweight presentational helpers
// ─────────────────────────────────────────────────────────────────────
const C = ({ children }) => (
    <code className="font-mono text-[12px] text-ux-text-1 bg-ux-bg-2 border border-ux-border-1 rounded px-1.5 py-0.5">
        {children}
    </code>
);

const Tip = ({ children, testId }) => (
    <div
        data-testid={testId}
        className="my-4 rounded-md border border-ux-accent/30 bg-ux-accent/5 p-3 text-[13px] text-ux-text-2"
    >
        <span className="font-mono text-[10px] uppercase tracking-wider text-ux-accent mr-2">
            TIP
        </span>
        {children}
    </div>
);

const Shot = ({ src, alt, caption }) => (
    <figure className="my-6 rounded-md border border-ux-border-1 bg-ux-bg-1 overflow-hidden help-shot">
        <img src={src} alt={alt} className="block w-full" />
        <figcaption className="px-3 py-2 text-[11px] font-mono text-ux-text-3 border-t border-ux-border-1">
            {caption}
        </figcaption>
    </figure>
);

const K = ({ keys }) => (
    <span className="font-mono text-[11px]">
        {keys.map((k, i) => (
            <span key={k}>
                {i > 0 ? <span className="mx-0.5 text-ux-text-3">+</span> : null}
                <kbd className="rounded border border-ux-border-1 bg-ux-bg-2 text-ux-text-1 px-1.5 py-0.5">
                    {k}
                </kbd>
            </span>
        ))}
    </span>
);

// ─────────────────────────────────────────────────────────────────────
// Section registry
// ─────────────────────────────────────────────────────────────────────
// Each section has a stable id (for deep-linking), a title, a lucide
// icon, a body function, and optional adminOnly flag. The body is a
// render-prop so we can share presentational helpers and thread the
// auth-context cleanly.
const SECTIONS = [
    {
        id: "getting-started",
        title: "Getting started",
        icon: LifeBuoy,
        body: () => (
            <>
                <p>
                    UAVSimNet is a counter-UAS simulation workbench. You sign in
                    with an email and password issued to your tenant, pick or
                    compose a scenario, kick off a Monte Carlo run, and walk out
                    with a reviewer-signed evidence record. This guide covers
                    the whole lifecycle end-to-end.
                </p>
                <h3 className="help-h3">Signing in and the tenant model</h3>
                <p>
                    Each organisation gets its own tenant. Your account lives
                    inside one tenant and cannot see data belonging to another.
                    Inside the tenant, four roles are available:{" "}
                    <C>operator</C> (analyst), <C>reviewer</C>, <C>admin</C>,
                    and <C>superadmin</C>. The superadmin role is reserved for
                    UAVSimNet platform operators and is not granted to
                    customers. Ask your tenant admin for an invite link; you
                    set your own password from the invite flow.
                </p>
                <h3 className="help-h3">Your first scenario in 5 minutes</h3>
                <ol className="help-ol">
                    <li>
                        Open <C>Plan → Scenarios</C>. Click{" "}
                        <C>Build a new preset</C> or open one of the seeded
                        preset scenarios and pick{" "}
                        <C>Duplicate as mine</C>.
                    </li>
                    <li>
                        Follow the breadcrumb into the Workbench. On the left
                        rail you will see the seven capability sections:
                        airframes, environment, terrain + LOS, threats,
                        interceptor, engagement, runs.
                    </li>
                    <li>
                        Attach an airframe (e.g. <C>preset_coyote-class</C>),
                        an environment (<C>preset_env_clear_day</C>), a threat
                        preset, a guidance profile, and an engagement preset.
                        The evidence rail on the right updates in real time —
                        block-red means a validation rule is blocking the run.
                    </li>
                    <li>
                        When the evidence rail turns to "0 blocking", the{" "}
                        <C>Run</C> button in the top-right activates. Click
                        it. Give your run a name and confirm.
                    </li>
                    <li>
                        The run appears under <C>Run → History</C>. Click it
                        when <C>succeeded</C> to see the detail page with
                        metrics, charts, and the export-report buttons.
                    </li>
                </ol>
                <Tip testId="help-tip-getting-started">
                    If the <C>Run</C> button is greyed out, hover over the
                    blocking pill on the evidence rail — it tells you which
                    validation rule is failing and which section owns it.
                </Tip>
            </>
        ),
    },
    {
        id: "workbench",
        title: "The Workbench",
        icon: LayoutGrid,
        body: () => (
            <>
                <p>
                    The Workbench is the three-pane surface where a scenario
                    is composed and validated. Left rail is the{" "}
                    <strong>scenario tree</strong>; centre pane is the active
                    section editor; right rail is the <strong>evidence</strong>{" "}
                    panel showing blocking issues, warnings, and the parameter
                    provenance ledger.
                </p>
                <Shot
                    src="/help/screens/02-workbench-overview.png"
                    alt="Workbench three-pane layout with scenario tree, airframe pane, and evidence rail"
                    caption="Workbench · left rail (tree with per-section counts) · centre pane (active section) · right rail (evidence with blocking / warnings / provenance)."
                />
                <h3 className="help-h3">Parameter badges</h3>
                <p>
                    Every numerical parameter carries three badges: a{" "}
                    <strong>source</strong> tag (preset / user / imported /
                    calibrated), a <strong>confidence</strong> tag (validated /
                    provisional / unverified), and an optional{" "}
                    <strong>citation</strong> link. A provisional value will
                    not block a run — but reviewers see the class, and the
                    exported report calls it out.
                </p>
                <h3 className="help-h3">SI vs Imperial</h3>
                <p>
                    The workbench ships with SI as default. Switch via{" "}
                    <C>Plan → Scenarios → Preferences</C> or the user-menu{" "}
                    <C>Display units</C> toggle. The conversion is
                    display-only — stored values stay SI, so switching units
                    never mutates a run.
                </p>
                <h3 className="help-h3">Validation gate</h3>
                <p>
                    The gate runs on every edit. <strong>Blocking</strong>{" "}
                    issues are surfaced in red and must be fixed before a run
                    can start. <strong>Warnings</strong> are amber — they
                    record on the run report but do not block. The full list
                    of rules, with id, severity, and scope, lives in{" "}
                    <C>/api/sim/validation-rules</C> if you need to see the
                    catalogue programmatically.
                </p>
                <h3 className="help-h3">Cmd+K palette</h3>
                <p>
                    Press <K keys={["Ctrl", "K"]} /> (or{" "}
                    <K keys={["⌘", "K"]} /> on macOS) anywhere to open the
                    command palette. Fuzzy-search across scenarios, runs,
                    libraries, and global actions. Arrow keys to navigate,{" "}
                    <K keys={["↵"]} /> to jump.
                </p>
            </>
        ),
    },
    {
        id: "airframes",
        title: "Airframes",
        icon: Rocket,
        body: () => (
            <>
                <p>
                    Airframes are the flying platforms in the scenario —
                    interceptors and threats alike. Open{" "}
                    <C>Plan → Airframes</C> for the library. Presets are
                    read-only and system-owned; <C>Duplicate as mine</C>{" "}
                    copies the preset into your tenant so you can edit it.
                </p>
                <h3 className="help-h3">Class + role taxonomy</h3>
                <p>
                    Each airframe carries a <strong>class</strong> (fixed_wing
                    / multirotor / loitering_munition / kinetic_interceptor)
                    and, once attached to a scenario, a <strong>role</strong>{" "}
                    (interceptor / threat / target). The class is intrinsic
                    to the airframe; the role is attachment-specific.
                </p>
                <h3 className="help-h3">Verification Flight</h3>
                <p>
                    Every airframe runs a canned Verification Flight before it
                    is eligible to be attached to a scenario. The flight
                    exercises thrust-to-weight, centre-of-gravity envelope,
                    level cruise, and a hard turn — then classifies the
                    airframe as <C>verified</C>, <C>needs_review</C>, or{" "}
                    <C>failed</C>. The verdict is surfaced on the airframe
                    detail page and in the evidence rail.
                </p>
                <Tip testId="help-tip-verify">
                    Common Verification Flight failures: thrust-to-weight
                    below 1 (airframe cannot climb), CoG outside the envelope
                    (the aero model cannot compensate), missing control
                    coefficients. Open the airframe and check{" "}
                    <C>verification.issues[]</C> on the detail page.
                </Tip>
                <h3 className="help-h3">JSBSim XML round-trip</h3>
                <p>
                    Paste a JSBSim aircraft XML into the airframe import
                    wizard and the platform translates it into the internal
                    parameter model. On export, the same airframe is rendered
                    back to JSBSim XML for use with the standalone solver
                    outside UAVSimNet. Parameters that JSBSim does not
                    represent natively (source / confidence / citation) are
                    preserved in sidecar metadata.
                </p>
            </>
        ),
    },
    {
        id: "environments",
        title: "Environments",
        icon: Wind,
        body: () => (
            <>
                <p>
                    The environment block covers atmosphere, wind,
                    turbulence, gusts, visibility, and solar position. Every
                    parameter flows through into the aerodynamic solver and
                    the EO/IR seeker chain.
                </p>
                <h3 className="help-h3">Atmosphere</h3>
                <p>
                    Pick <C>ISA</C> for the International Standard
                    Atmosphere (sea-level 15 °C, 1013.25 hPa) or{" "}
                    <C>non_standard</C> for custom temperature and pressure
                    offsets — useful for summer desert, winter Arctic, or
                    high-altitude scenarios. Humidity affects EO/IR extinction.
                </p>
                <h3 className="help-h3">Wind profiles</h3>
                <p>
                    Three options: <C>constant</C> (one vector everywhere),{" "}
                    <C>log_law</C> (boundary-layer growth against a
                    surface-roughness length), and <C>power_law</C>{" "}
                    (simpler exponent form). Log-law is the default for
                    realistic low-altitude engagements.
                </p>
                <h3 className="help-h3">Turbulence</h3>
                <p>
                    <C>dryden</C> and <C>von_karman</C> are both supported.
                    Dryden is MIL-HDBK-1797 standard; Von Kármán is slightly
                    closer to measured atmospheric turbulence. Gust schedules
                    overlay a deterministic shape on top of the stochastic
                    turbulence so you can rehearse a specific wind event.
                </p>
                <h3 className="help-h3">Visibility and solar</h3>
                <p>
                    Visibility (m) degrades EO/IR detection distance. Solar
                    position is computed via NOAA SPA from latitude, longitude,
                    date, and time-of-day. If an EO seeker points within the
                    solar-blind cone, the validation gate flags a warning.
                </p>
                <Tip testId="help-tip-env-presets">
                    Three seeded presets cover the common cases:{" "}
                    <C>preset_env_clear_day</C> (ISA, calm wind, high
                    visibility), <C>preset_env_contested_weather</C>{" "}
                    (moderate wind, reduced visibility, gusts), and{" "}
                    <C>preset_env_urban_canyon</C> (low wind, scattered obs,
                    typical of city centres).
                </Tip>
            </>
        ),
    },
    {
        id: "terrain-los",
        title: "Terrain + RF line-of-sight",
        icon: Mountain,
        body: () => (
            <>
                <p>
                    Attach a terrain asset and a set of obstacles, and the
                    deterministic line-of-sight raycaster propagates the
                    consequences into the seeker and comms-denied triggers.
                </p>
                <h3 className="help-h3">DEM import</h3>
                <p>
                    Use the OpenTopography picker to fetch a tile for your
                    engagement box; the platform caches the GeoTIFF under the
                    terrain-asset record. An SRTM fallback is available for
                    regions that OpenTopography does not cover. Tiles can also
                    be uploaded directly if you hold the data on-premises.
                </p>
                <h3 className="help-h3">Obstacles</h3>
                <p>
                    Obstacles are GeoJSON features — polygons for buildings,
                    boxes for no-fly zones, lines for mast arrays. Drag and
                    drop a GeoJSON file onto the obstacle library or draw
                    features directly on the terrain map.
                </p>
                <h3 className="help-h3">RF line-of-sight</h3>
                <p>
                    The raycaster takes a DEM + obstacle set and produces (a) a
                    point-to-point LOS check (boolean, with reason), (b) a
                    visibility grid (a 2D map of "can this pixel see the
                    launcher"), and (c) an RF heatmap overlay on the Workbench
                    Terrain pane. The model uses a 4/3-Earth curvature
                    correction so beyond-line-of-sight links resolve the way a
                    real RF link would.
                </p>
                <Tip testId="help-tip-los">
                    Per-tick LOS events feed directly into the seeker and the
                    comms-denied triggers — a threat that runs behind a ridge
                    will break seeker lock at the right tick and, if configured
                    with <C>c2_loss_trigger = on_detection</C>, pivot onto its
                    fallback waypoints.
                </Tip>
            </>
        ),
    },
    {
        id: "threats",
        title: "Threats",
        icon: Radar,
        body: () => (
            <>
                <p>
                    Six doctrinal threat modes ship in the library. Each has
                    its own schema and its own validation rules — no hybrid
                    can be assembled by accident.
                </p>
                <h3 className="help-h3">Single waypoint</h3>
                <p>
                    One ingress leg. Good for simple proof-of-concept
                    engagements and regression baselines.
                </p>
                <h3 className="help-h3">Patrol</h3>
                <p>
                    A closed loop of waypoints. The threat flies the loop
                    until the engagement concludes.
                </p>
                <h3 className="help-h3">Swarm</h3>
                <p>
                    Up to 25 agents in one of four formations —{" "}
                    <C>line</C>, <C>wedge</C>, <C>diamond</C>,{" "}
                    <C>column</C>. Spacing is validated at save time so you
                    cannot commit a formation whose agents overlap.
                </p>
                <h3 className="help-h3">Evasive</h3>
                <p>
                    Weave, jink schedule, and terrain-hug. The jink-schedule
                    editor lives inside the Workbench Threats pane — add an
                    event, set the time, duration, lateral acceleration, and
                    axis, and the sparkline updates live.
                </p>
                <h3 className="help-h3">Comms-denied</h3>
                <p>
                    Target loses C2 link at either a time offset{" "}
                    (<C>trigger = time</C>) or the first moment the seeker
                    detects it (<C>trigger = on_detection</C>). At the trigger
                    tick the target pivots onto its fallback waypoints — the
                    diagnostic scrubber marks this with the purple chevron.
                </p>
                <h3 className="help-h3">Loitering terminal commit</h3>
                <p>
                    A loitering munition that circles until a commit cue, then
                    dives onto the target. Use this for one-way effects
                    against fixed installations.
                </p>
            </>
        ),
    },
    {
        id: "guidance",
        title: "Interceptor guidance + seeker",
        icon: Crosshair,
        body: () => (
            <>
                <p>
                    Guidance profiles pair a <strong>guidance law</strong>{" "}
                    with a <strong>seeker model</strong> and a set of{" "}
                    <strong>lock-loss rules</strong>. All three live on the
                    same profile and are versioned together.
                </p>
                <h3 className="help-h3">Guidance laws</h3>
                <ul className="help-ul">
                    <li>
                        <strong>Pure Pursuit</strong> — heading always points
                        at the target. Simple, robust, high miss distance at
                        crossing geometries.
                    </li>
                    <li>
                        <strong>Proportional Navigation (PN)</strong> — the
                        classic. Navigation constant <C>N</C> between 3 and 5;
                        the validation gate rejects values outside that band.
                    </li>
                    <li>
                        <strong>Augmented PN (APN)</strong> — PN with a
                        target-acceleration estimator (alpha-beta or Kalman).
                        Better against manoeuvring targets.
                    </li>
                    <li>
                        <strong>Command-to-Line-of-Sight (CLOS)</strong> — the
                        interceptor flies the beam; useful against short-range,
                        low-altitude threats where seeker acquisition is
                        marginal.
                    </li>
                </ul>
                <h3 className="help-h3">Seeker models</h3>
                <p>
                    RF and EO/IR. Both expose detection range, field-of-view,
                    tracking noise, signal-to-noise threshold, and a lock-loss
                    envelope. EO/IR additionally respects solar blind-angle,
                    visibility, and target infrared signature. When a seeker
                    loses lock, the reacquire pattern determines whether it
                    scans the last-known cone, spirals out, or falls back to
                    comm-assisted cueing.
                </p>
                <h3 className="help-h3">Guidance Compare</h3>
                <p>
                    From the Workbench Interceptor pane, pick between 2 and 4
                    guidance profiles and click <C>Compare</C>. The same
                    Monte Carlo seeds are driven through every profile and the
                    results are rendered as a bar chart of mean miss with 1σ
                    error-bars. If an engagement is attached, a p(kill)
                    overlay appears on a secondary axis and a{" "}
                    <strong>Best Profile</strong> card summarises the verdict.
                </p>
                <h3 className="help-h3">Diagnostic scrubber</h3>
                <p>
                    On a completed run, open the diagnostic harness drawer.
                    Drag the scrubber through time to see the interceptor and
                    target state. Four chevrons mark key events:
                </p>
                <ul className="help-ul">
                    <li>
                        <strong>Green</strong> — first detection
                    </li>
                    <li>
                        <strong>Red</strong> — first lock loss
                    </li>
                    <li>
                        <strong>Blue</strong> — first re-acquisition
                    </li>
                    <li>
                        <strong>Purple</strong> — C2 loss (threat pivoted onto
                        its fallback waypoints)
                    </li>
                </ul>
            </>
        ),
    },
    {
        id: "engagement",
        title: "Engagement + lethality",
        icon: Target,
        body: () => (
            <>
                <p>
                    Attach an engagement to move from "did we intercept?" to
                    "did we kill the thing?". The engagement ties together a
                    warhead, a fuze, a probability-of-kill curve, and a salvo
                    doctrine.
                </p>
                <Shot
                    src="/help/screens/04-plan-engagements.png"
                    alt="Engagements library with four preset engagements listed"
                    caption="Plan → Engagements — four seeded presets covering blast-frag, kinetic, net capture, and a twin-shot salvo doctrine."
                />
                <h3 className="help-h3">Warheads</h3>
                <p>
                    Four types: blast-fragmentation, kinetic impact,
                    net-capture, directed energy. Each carries a lethal
                    radius, an effective mass, and (for blast-frag) a fragment
                    pattern (isotropic or forward-cone).
                </p>
                <h3 className="help-h3">Fuzing</h3>
                <p>
                    Three modes: proximity, contact, and time-after-launch.
                    Each mode has an arming rule — the validation gate blocks
                    a configuration whose trigger fires before the fuze has
                    armed (e.g. proximity with arming_distance &gt; trigger
                    distance).
                </p>
                <h3 className="help-h3">Pk curves</h3>
                <p>
                    A Pk curve is a lookup of kill probability against miss
                    distance. Curves must be monotonic non-increasing — the
                    editor warns live and the validation rule{" "}
                    <C>pk_curve_monotonic_non_increasing</C> blocks a save
                    that fails the rule. The interpolator clamps below the
                    first point and above the last.
                </p>
                <h3 className="help-h3">Salvo doctrines</h3>
                <ul className="help-ul">
                    <li>
                        <strong>Single</strong> — one shot.
                    </li>
                    <li>
                        <strong>Salvo</strong> — N shots at a fixed spacing.
                        Every shot evaluates independently against its own
                        miss distance.
                    </li>
                    <li>
                        <strong>Shoot-Look-Shoot (SLS)</strong> — fire,
                        evaluate kill over a BDA window, re-shoot if the
                        target is still alive.
                    </li>
                    <li>
                        <strong>Shoot-Shoot-Look (SSL)</strong> — fire two,
                        evaluate both over a BDA window, re-shoot only the
                        remainder.
                    </li>
                </ul>
                <h3 className="help-h3">Pk overlay</h3>
                <p>
                    When an engagement is attached and you click{" "}
                    <C>Compare</C> on the Guidance Compare tool, a p(kill)
                    line is overlaid on the miss-distance bars. The seeds
                    driving the Pk overlay are identical to the seeds driving
                    the bars, so the two charts are always consistent.
                </p>
            </>
        ),
    },
    {
        id: "running",
        title: "Running a simulation",
        icon: PlayCircle,
        body: () => (
            <>
                <p>
                    When the evidence rail reads <C>0 BLOCKING</C>, click{" "}
                    <C>Run</C> in the top-right. Confirm the name and seed
                    count and the run kicks off as a background job. The
                    header shows a live run counter.
                </p>
                <h3 className="help-h3">Kinematic vs JSBSim-hybrid</h3>
                <p>
                    Two backends are available. <C>kinematic</C> is a fast
                    point-mass integrator — good for Monte Carlo sweeps in the
                    thousands-of-seeds range. <C>jsbsim_hybrid</C> drives the
                    full 6-DoF solver for interceptor dynamics while keeping
                    the kinematic target model — the compromise gives you
                    credible aero without the compute cost of two 6-DoF
                    simulations running against each other.
                </p>
                <Tip testId="help-tip-backend-pick">
                    If the attached environment has{" "}
                    <C>turbulence.enabled = true</C>, the kinematic backend
                    is blocked (it cannot propagate turbulence) and the
                    validation rule{" "}
                    <C>turbulence_requires_kinematic_or_hybrid</C> tells you
                    so.
                </Tip>
                <h3 className="help-h3">Reading the run detail page</h3>
                <p>
                    KPIs along the top: seed count, mean miss, 1σ miss, 95 %
                    CEP, outcome counts (kill / miss / no-intercept). Below
                    that: the trajectory plot (interceptor vs target in
                    world frame), link-uptime over time, detection-event
                    timeline. Further down: the parameter-provenance snapshot
                    (immutable), and the export-report controls (PDF + DOCX).
                </p>
                <Shot
                    src="/help/screens/05-run-history.png"
                    alt="Run history list with succeeded runs and actions"
                    caption="Run → History — every run your tenant has executed, with status, type, and quick-jump back to the Workbench."
                />
            </>
        ),
    },
    {
        id: "review",
        title: "Review workflow",
        icon: ShieldCheck,
        body: () => (
            <>
                <p>
                    Runs move through a five-state machine:{" "}
                    <C>draft</C> → <C>submitted</C> →{" "}
                    <C>approved</C> / <C>rejected</C> /{" "}
                    <C>changes_requested</C>. Only <C>approved</C> runs can be
                    promoted to a <strong>golden reference</strong>.
                </p>
                <h3 className="help-h3">Submit for review</h3>
                <p>
                    On a <C>succeeded</C> run, the analyst clicks{" "}
                    <C>Submit for review</C>. Add optional submission notes.
                    The run enters the reviewer inbox and is locked against
                    analyst edits.
                </p>
                <h3 className="help-h3">Approve / request changes / reject</h3>
                <p>
                    Reviewers and admins see three actions on a{" "}
                    <C>submitted</C> run. <C>Approve</C> sends the run to the
                    approved state. <C>Request changes</C> kicks it back to
                    the analyst with reviewer notes — the analyst can edit
                    and re-submit. <C>Reject</C> is a hard stop — the run is
                    marked rejected and cannot be re-submitted as-is.
                </p>
                <h3 className="help-h3">Promote to golden</h3>
                <p>
                    An approved run can be promoted to a golden reference.
                    Subsequent runs of the same scenario are compared against
                    the golden baseline and any drift is surfaced on the run
                    detail page.
                </p>
                <h3 className="help-h3">Parameter provenance snapshot</h3>
                <p>
                    At submit time, every parameter that fed the run is
                    captured into an <strong>immutable snapshot</strong>. It
                    cannot be mutated by later library edits. The run detail
                    page exposes it via the{" "}
                    <C>Parameter Provenance</C> section, grouped by
                    collection, with source + confidence + citation per row.
                </p>
            </>
        ),
    },
    {
        id: "reports",
        title: "Reports",
        icon: FileText,
        body: () => (
            <>
                <p>
                    Every run produces three artefacts on demand: the
                    compiled scenario (YAML), the numerical record (JSON +
                    CSV), and a navy-themed decision document in PDF and
                    DOCX.
                </p>
                <h3 className="help-h3">PDF / DOCX report</h3>
                <p>
                    Click <C>Export report</C> on the run detail page. The
                    report sections, in order:
                </p>
                <ol className="help-ol">
                    <li>
                        <strong>Cover</strong> — title, run id, submitted-by,
                        reviewer, review state.
                    </li>
                    <li>
                        <strong>Capability summary</strong> — one-paragraph
                        plain-English narrative of what was tested.
                    </li>
                    <li>
                        <strong>Results</strong> — KPI table, trajectory
                        plot, detection timeline, guidance-compare chart (if
                        a compare was run).
                    </li>
                    <li>
                        <strong>Engagement outcome</strong> — if an engagement
                        was attached, the Pk table, outcome counts, and the
                        Best-Profile summary.
                    </li>
                    <li>
                        <strong>Parameter provenance</strong> — the full
                        snapshot, grouped by collection, source + confidence +
                        citation per row.
                    </li>
                    <li>
                        <strong>Validation ledger</strong> — every rule run
                        against the scenario, severity, and verdict.
                    </li>
                    <li>
                        <strong>Sign-off block</strong> — analyst, reviewer,
                        date, review notes.
                    </li>
                </ol>
                <Tip testId="help-tip-reports">
                    Reports are rendered server-side (WeasyPrint for PDF,
                    python-docx for DOCX) from the same compiled scenario that
                    fed the run, so a report and its numerical record can
                    never disagree.
                </Tip>
            </>
        ),
    },
    {
        id: "design-optimisation",
        title: "Design optimisation studies",
        icon: Crosshair,
        body: () => (
            <>
                <p>
                    A <strong>design optimisation study</strong> searches the
                    design space against a baseline scenario and recommends a
                    configuration that hits a Pk target at minimum design cost.
                    Open <C>Run → Design Optimisation</C> to start one, or kick
                    off a study from the Workbench's "Design Optimisation" tab.
                </p>
                <h3 className="help-h3">Composing a study</h3>
                <ol className="help-ol">
                    <li>
                        <strong>Pick a baseline scenario.</strong> Presets and
                        your own draft scenarios are eligible. Quick-Sim and
                        matrix-cell scenarios are filtered out.
                    </li>
                    <li>
                        <strong>Pick an objective.</strong> {" "}
                        <C>Maximise Pk</C> for an open-ended search,
                        <C>Reach a target Pk</C> when you have a Pk floor,
                        <C>Minimise cost for a target Pk</C> when budget is the
                        constraint.
                    </li>
                    <li>
                        <strong>Pick design variables.</strong> The registry
                        (admin-curated) lists ~18 levers across the four design
                        categories. Each carries an <C>override_mode</C> badge —
                        engine_inline variables splice into the engine's
                        optional_overrides; scenario_doc variables write into
                        the ephemeral candidate's parameters block before
                        compile.
                    </li>
                    <li>
                        <strong>Set constraints.</strong> Range constraints
                        mirror each variable's band; relational rules let you
                        couple two variables (e.g. interceptor.max_speed_mps
                        ≥ target.max_speed_mps); the cost-budget slider caps
                        the aggregate weighted-sum delta.
                    </li>
                    <li>
                        <strong>Pick a compute profile.</strong> {" "}
                        <C>Fast</C> (~25 candidates, interactive),
                        <C>Balanced</C> (100–300, recommended for production),
                        <C>Rigorous</C> (overnight — the robustness mode uses this for
                        Pareto frontiers and confirmation runs).
                    </li>
                </ol>
                <h3 className="help-h3">What the pipeline does</h3>
                <ol className="help-ol">
                    <li>
                        <strong>Baseline run.</strong> The unmodified scenario
                        is run first so the recommended configuration is
                        always reported as a delta against a known anchor.
                    </li>
                    <li>
                        <strong>Local sensitivity (OAT).</strong> Each variable
                        gets pushed to its band edges to produce the tornado
                        chart you see on the detail page.
                    </li>
                    <li>
                        <strong>Latin Hypercube sampling.</strong> The
                        deterministic LHS sampler produces N candidates
                        (N depends on the profile). Constraint-violating
                        candidates are dropped before the engine sees them.
                    </li>
                    <li>
                        <strong>Parallel candidate evaluation.</strong> Each
                        candidate becomes an ephemeral scenario, runs through
                        the standard Monte Carlo engine, and writes back to
                        the per-tenant cache.
                    </li>
                    <li>
                        <strong>Ranking.</strong> The objective decides the
                        sort key. The top candidate is the recommended
                        configuration; the BaselineVsBestCard is the hero you
                        screenshot for procurement review.
                    </li>
                </ol>
                <h3 className="help-h3">Design cost</h3>
                <p>
                    Each variable carries a <C>cost_weight</C> in the registry
                    (admin-tunable). The candidate's design cost is a weighted
                    sum of |delta / band-width| across all selected variables.
                    A cost of 0 means the candidate is the baseline; a cost of
                    1 means a significant configuration change. The
                    cost-budget constraint on the composer rejects candidates
                    above a threshold so you only see economically viable
                    designs.
                </p>
                <h3 className="help-h3">Cache hits</h3>
                <p>
                    The optimisation cache is keyed on
                    <C>(tenant_id, candidate_hash)</C>. The hash is stable
                    across studies and tenants — if you re-run a study with
                    the same variables and bands, every candidate that lands
                    on a previously-evaluated configuration is marked
                    <C>cached</C> and short-circuits the engine. This is what
                    makes the Balanced profile feasible without
                    burning a child-run budget.
                </p>
                <h3 className="help-h3">Promotion semantics</h3>
                <p>
                    Clicking <C>Promote configuration</C> creates a fresh,
                    non-ephemeral <C>sim_scenarios</C> document with the
                    candidate's deltas baked into <C>parameters</C>. The new
                    scenario carries <C>provenance.source = "roe_promotion"</C>
                    plus the study + candidate IDs, lands you at
                    <C>/workbench/&lt;new_id&gt;</C>, and runs through the
                    standard Workbench pipeline. The promoted scenario passes
                    the validation gate before persistence — if it would have
                    been blocking, the modal stays open with the findings.
                </p>
                <h3 className="help-h3">How Pk confidence is reported</h3>
                <p>
                    Every Pk reading on a study now carries a confidence
                    interval derived from the binomial standard error and the
                    number of Monte Carlo seeds the run used. Two candidates
                    are reported as statistically indistinguishable when their
                    intervals overlap; the recommended candidate is also
                    flagged as statistically distinct from the baseline (or
                    not). When several top candidates fall inside the same
                    statistical bucket, they are surfaced as tie-bucket peers
                    so the analyst can pick on cost or other secondary
                    factors rather than chase noise.
                </p>
                <h3 className="help-h3">How robustness is scored</h3>
                <p>
                    A robustness study runs every candidate at three
                    environment perturbations (the baseline wind, a stronger
                    wind, and a weaker one) and combines the three Pk
                    readings into a single score that trades the mean against
                    the spread. A higher score means a configuration that
                    performs well across the perturbations on average without
                    being too sensitive to which one you actually face. The
                    weighting between mean and spread is admin-tunable per
                    tenant.
                </p>
                <h3 className="help-h3">How the recommendation score is built</h3>
                <p>
                    Alongside the objective-pure best, the workbench surfaces
                    a balanced "recommendation score" combining the Pk uplift
                    over baseline, the design efficiency, the statistical
                    confidence (interval tightness, seed count, model
                    credibility) and a residual-risk penalty drawn from the
                    candidate's failure-mode profile. The four coefficients
                    are admin-tunable per tenant. The risk-penalty term uses
                    a coarse projection of failure modes in v1; a richer
                    per-seed distribution is planned.
                </p>
                <h3 className="help-h3">What confidence intervals on Pk mean</h3>
                <p>
                    Each Pk figure is an estimate from a finite seed budget.
                    The 95 % confidence interval shown next to it as ±x is
                    the half-width of the binomial uncertainty band. When two
                    candidates' confidence intervals overlap, they are not
                    statistically distinguishable at the current seed budget,
                    and the procurement evaluator should rerun the study with
                    more seeds before declaring a winner. The hero card
                    flags this with a small "within statistical noise" pill.
                </p>
                <h3 className="help-h3">How robustness is scored</h3>
                <p>
                    Robustness is mean Pk minus λ times the standard
                    deviation of the per-perturbation Pk readings, with λ
                    set to 0.5 by default. A tighter spread is rewarded;
                    a configuration that hits 0.92 reliably across all
                    three wind perturbations beats one that averages 0.95
                    but swings between 0.70 and 1.00. The composition
                    formula and the perturbation magnitude are
                    admin-tunable per tenant.
                </p>
                <h3 className="help-h3">How the composite recommendation score works</h3>
                <p>
                    The composite score is a weighted sum of four
                    sub-terms: uplift (a, default 0.4), efficiency (b, 0.3),
                    confidence (c, 0.2) and risk-penalty (d, 0.1). The
                    risk-penalty subtracts rather than adds, and it draws
                    from the projected failure-mode distribution explained
                    above. Two candidates with identical objective scores
                    but different residual-risk profiles will rank apart;
                    the leading candidate's tie-bucket peers are listed
                    explicitly so staff can apply procurement judgement
                    where the math says the difference is statistically
                    invisible.
                </p>
                <h3 className="help-h3">Reading the Pareto frontier</h3>
                <p>
                    The Pareto frontier scatter plots every candidate as a
                    dot at its design cost and probability of kill. The
                    teal line connects configurations that are
                    non-dominated — no other candidate is both cheaper and
                    more lethal. The cream diamond marks the knee point:
                    the configuration where additional design spend stops
                    buying meaningful Pk uplift. Configurations to the
                    right of the knee carry diminishing returns; those to
                    the left fall short on Pk. The cream star marks the
                    baseline at zero design cost.
                </p>
                <h3 className="help-h3">What confirmation runs do</h3>
                <p>
                    Once the primary search ranks the top candidates, the
                    workbench re-runs the leaders against a higher seed
                    budget to tighten the confidence interval on Pk. The
                    confirmed Pk is shown alongside the predicted Pk on
                    the recommended-configuration card and on every
                    candidate that passed through the confirmation pass.
                    A "Diverged" pill flags the rare cases where the
                    confirmation Pk falls outside the predicted confidence
                    interval — a signal to review the candidate before
                    promotion. The recommendation-basis breadcrumb
                    summarises which path produced the final
                    recommendation: primary rank only, confirmed at higher
                    fidelity, re-elected by the confirmation pass, or
                    cancelled mid-confirmation.
                </p>
                <h3 className="help-h3">Downloading the Design Optimisation Report</h3>
                <p>
                    Once a study completes, the right-hand panel offers a
                    "Generate report" action that produces a procurement-
                    grade PDF and a matching DOCX. Both formats embed the
                    cover, executive summary, baseline scenario, model
                    credibility ladder, sensitivity tornado, Pareto
                    frontier, recommended configuration with variable
                    deltas, alternative configurations, residual caveats,
                    and an artefact appendix. Regenerating overwrites the
                    files in place; the existing artefact list keeps a
                    single row per format with the latest size and
                    SHA-256.
                </p>
                <Tip testId="help-tip-roe-confirm">
                    The robustness mode adds confirmation runs (a fixed number of
                    extra seeds against the recommended candidate to tighten
                    the Pk confidence interval) and Pareto-frontier ranking
                    for studies with multiple competing objectives.
                </Tip>
            </>
        ),
    },
    {
        id: "admin",
        title: "Admin features",
        icon: UserCog,
        adminOnly: true,
        body: () => (
            <>
                <p>
                    These features are visible only to tenant admins and
                    platform superadmins. Analysts and reviewers will not see
                    this section in the sidebar.
                </p>
                <h3 className="help-h3">Tenant management</h3>
                <p>
                    <C>Admin → Tenants</C> lists every tenant the platform
                    knows about. Superadmins can create and suspend tenants;
                    tenant-admins can only see their own tenant. Each tenant
                    carries a subscription tier (free, evaluator, operator,
                    enterprise) that governs seat count, run concurrency, and
                    retention period.
                </p>
                <h3 className="help-h3">User roles</h3>
                <p>
                    <C>Admin → Users</C> is the invite + role management
                    surface. Issue an invite email with a role preset; the
                    user sets their own password from the invite link. Roles
                    can be edited after the fact; disabling a user immediately
                    invalidates every token they hold.
                </p>
                <h3 className="help-h3">Audit events</h3>
                <p>
                    Every login, password change, run submit, review
                    transition, and admin action is recorded to{" "}
                    <C>auth_events</C>. The <C>Admin → Engineering</C>{" "}
                    console lets you filter by user, type, and time range. TTL
                    defaults to 90 days; contact a platform operator if your
                    retention policy is longer.
                </p>
                <h3 className="help-h3">Access requests</h3>
                <p>
                    The public <C>/contact</C> form writes to the{" "}
                    <C>access_requests</C> collection. Admins triage pending
                    requests from <C>Admin → Access requests</C>, accept or
                    reject, and the accept flow automatically provisions a
                    tenant and sends an invite email.
                </p>
            </>
        ),
    },
    {
        id: "shortcuts",
        title: "Keyboard shortcuts + Cmd+K tips",
        icon: Keyboard,
        body: () => (
            <>
                <p>Global shortcuts (work anywhere in the authenticated console):</p>
                <table className="help-table">
                    <thead>
                        <tr>
                            <th>Shortcut</th>
                            <th>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td><K keys={["Ctrl", "K"]} /> / <K keys={["⌘", "K"]} /></td>
                            <td>Open command palette</td>
                        </tr>
                        <tr>
                            <td><K keys={["G"]} /> then <K keys={["S"]} /></td>
                            <td>Go to Plan → Scenarios</td>
                        </tr>
                        <tr>
                            <td><K keys={["G"]} /> then <K keys={["R"]} /></td>
                            <td>Go to Run → History</td>
                        </tr>
                        <tr>
                            <td><K keys={["G"]} /> then <K keys={["A"]} /></td>
                            <td>Go to Plan → Airframes</td>
                        </tr>
                        <tr>
                            <td><K keys={["?"]} /></td>
                            <td>Open this help page (<C>/help</C>)</td>
                        </tr>
                        <tr>
                            <td><K keys={["Esc"]} /></td>
                            <td>Dismiss the active dialog or palette</td>
                        </tr>
                    </tbody>
                </table>
                <h3 className="help-h3">Cmd+K tips</h3>
                <ul className="help-ul">
                    <li>Type a scenario name to jump straight to its Workbench.</li>
                    <li>
                        Type <C>run history</C> to open the runs list, or <C>open</C>{" "}
                        a scenario and press <C>Run</C> from its header.
                    </li>
                    <li>
                        Type <C>airframe</C> or <C>threat</C> to filter the
                        library lists without changing pages.
                    </li>
                    <li>
                        The palette remembers your last 10 deep-links; arrow-up
                        from an empty query to see them.
                    </li>
                </ul>
            </>
        ),
    },
    {
        id: "glossary",
        title: "Glossary",
        icon: BookOpen,
        body: () => (
            <>
                <p>One-liner definitions for the acronyms that appear on the Workbench and in reports:</p>
                <dl className="help-dl">
                    <dt>APN</dt><dd>Augmented Proportional Navigation — PN plus a target-acceleration estimator (alpha-beta or Kalman).</dd>
                    <dt>BDA</dt><dd>Battle Damage Assessment — the window in which a shoot-look-shoot doctrine evaluates whether the target is still alive.</dd>
                    <dt>CEP</dt><dd>Circular Error Probable — the radius that contains 50 % of the miss distances. <C>CEP95</C> is the 95 % variant.</dd>
                    <dt>CLOS</dt><dd>Command-to-Line-of-Sight — guidance law where the interceptor flies the line of sight from the launcher to the target.</dd>
                    <dt>DEM</dt><dd>Digital Elevation Model — the raster terrain we drape the engagement over.</dd>
                    <dt>Dryden</dt><dd>Atmospheric turbulence model from MIL-HDBK-1797, a standard in flight-dynamics work.</dd>
                    <dt>FoV</dt><dd>Field of View — the solid angle a seeker can see without slewing.</dd>
                    <dt>ISA</dt><dd>International Standard Atmosphere — sea-level 15 °C and 1013.25 hPa, used as the default when no custom atmosphere is specified.</dd>
                    <dt>LOS</dt><dd>Line of sight — does a straight line from A to B clear all terrain and obstacles?</dd>
                    <dt>MC</dt><dd>Monte Carlo — sampling an uncertain system many times to estimate a distribution over outcomes.</dd>
                    <dt>PN</dt><dd>Proportional Navigation — the classic short-range-intercept guidance law, navigation constant usually between 3 and 5.</dd>
                    <dt>Pk</dt><dd>Probability of Kill — the odds that a single engagement destroys the target.</dd>
                    <dt>SLS</dt><dd>Shoot-Look-Shoot — doctrine that fires, assesses over a BDA window, and re-shoots if needed.</dd>
                    <dt>SNR</dt><dd>Signal-to-Noise Ratio — used here as the seeker's detection threshold.</dd>
                    <dt>SPA</dt><dd>Solar Position Algorithm — NOAA's reference for computing sun elevation and azimuth given latitude, longitude, and time.</dd>
                    <dt>SSL</dt><dd>Shoot-Shoot-Look — doctrine that fires two, assesses over a BDA window, and re-shoots only the remainder if needed.</dd>
                    <dt>T/W</dt><dd>Thrust-to-Weight — ratio of available thrust to weight at sea level; must exceed 1 for an airframe to climb.</dd>
                    <dt>Von Kármán</dt><dd>Atmospheric turbulence model, slightly closer to measured turbulence than Dryden.</dd>
                </dl>
            </>
        ),
    },
    {
        id: "deferred",
        title: "Deferred and known limitations",
        icon: FlaskConical,
        body: () => (
            <>
                <p>
                    An honest list of known limitations that the validation
                    gate protects you from, but that are worth knowing.
                </p>
                <h3 className="help-h3">JSBSim HybridFDM wind-property splicing</h3>
                <p>
                    The JSBSim hybrid backend does not yet splice environment
                    wind properties into JSBSim's native atmosphere model — the
                    kinematic target uses the full wind, the JSBSim
                    interceptor uses zero wind. The validation rule{" "}
                    <C>turbulence_requires_kinematic_or_hybrid</C> blocks
                    combinations that would mislead an operator. Scheduled
                    work for a future module.
                </p>
                <h3 className="help-h3">Module 8 polish</h3>
                <ul className="help-ul">
                    <li>
                        PDF Parameter Provenance section renderer — the
                        snapshot is captured and queryable, and the JSON
                        export is complete, but the inline PDF section is
                        still the condensed summary.
                    </li>
                    <li>
                        Command Palette per-collection recents — the palette
                        supports a global recents list, but not per-category.
                    </li>
                    <li>
                        RF heatmap resolution slider — heatmaps currently
                        render at a single server-side resolution.
                    </li>
                    <li>
                        Empty-state + error-boundary consistency pass across
                        every library page.
                    </li>
                </ul>
                <p className="text-[12px] text-ux-text-3 mt-3">
                    Anything technical on this list can be followed up on in{" "}
                    <C>/app/memory/PRD.md</C> §9 (TODO list).
                </p>
            </>
        ),
    },
];

// ─────────────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────────────
export default function HelpPage() {
    const { isAdmin, user } = useAuth();
    const [query, setQuery] = useState("");
    const [activeId, setActiveId] = useState(SECTIONS[0].id);
    const sectionsRef = useRef({});

    // Hide admin-only sections from non-admin readers. Admins see
    // everything (we don't do a two-stage load).
    const visibleSections = useMemo(
        () => SECTIONS.filter((s) => !s.adminOnly || isAdmin),
        [isAdmin],
    );

    // Client-side search — filter TOC + section list by substring match
    // against title + rendered body text (case-insensitive).
    const q = query.trim().toLowerCase();
    const filtered = useMemo(() => {
        if (!q) return visibleSections;
        return visibleSections.filter((s) => {
            if (s.title.toLowerCase().includes(q)) return true;
            try {
                const el = sectionsRef.current[s.id];
                const haystack = (el?.textContent || "").toLowerCase();
                return haystack.includes(q);
            } catch (_e) {
                return false;
            }
        });
    }, [visibleSections, q]);

    // Observe sections for scroll-spy active id.
    useEffect(() => {
        const root = document.querySelector("#help-main");
        if (!root || typeof IntersectionObserver === "undefined") return undefined;
        const obs = new IntersectionObserver(
            (entries) => {
                const vis = entries
                    .filter((e) => e.isIntersecting)
                    .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
                if (vis.length) setActiveId(vis[0].target.id);
            },
            { root: null, rootMargin: "-30% 0% -60% 0%", threshold: [0, 0.3, 0.6] },
        );
        visibleSections.forEach((s) => {
            const el = sectionsRef.current[s.id];
            if (el) obs.observe(el);
        });
        return () => obs.disconnect();
    }, [visibleSections]);

    // Handle ?q= query reflect (optional) and scroll to the hash target.
    useEffect(() => {
        const hash = window.location.hash.replace("#", "");
        if (hash && sectionsRef.current[hash]) {
            sectionsRef.current[hash].scrollIntoView({ block: "start" });
            setActiveId(hash);
        }
    }, []);

    return (
        <div className="help-page" data-testid="help-page">
            <header className="help-header">
                <div>
                    <span className="help-eyebrow">// INTERNAL USER GUIDE</span>
                    <h1 className="help-h1">UAVSimNet operator guide</h1>
                    <p className="help-lede">
                        Hands-on operator documentation for every capability
                        surface on the platform. Signed in as{" "}
                        <C>{user?.username || "—"}</C>
                        {user?.role ? (
                            <>
                                {" · role "}
                                <C>{user.role}</C>
                            </>
                        ) : null}
                        .
                    </p>
                </div>
                <div className="help-actions">
                    <button
                        type="button"
                        data-testid="help-print-btn"
                        onClick={() => window.print()}
                        className="inline-flex items-center gap-2 text-[12px] font-mono text-ux-accent hover:underline"
                    >
                        <Printer className="w-3.5 h-3.5" /> Print
                    </button>
                </div>
            </header>

            <div className="help-body">
                {/* ── Sticky TOC ─────────────────────────────────────── */}
                <aside className="help-toc" data-testid="help-toc">
                    <div className="help-toc-search">
                        <Search className="w-3.5 h-3.5 text-ux-text-3" />
                        <input
                            type="search"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Filter sections…"
                            data-testid="help-search"
                            className="bg-transparent outline-none text-[12px] font-mono text-ux-text-1 w-full"
                        />
                    </div>
                    <nav className="help-toc-list">
                        {filtered.map((s) => {
                            const Icon = s.icon;
                            return (
                                <a
                                    key={s.id}
                                    href={`#${s.id}`}
                                    data-testid={`help-toc-${s.id}`}
                                    className={`help-toc-link ${
                                        activeId === s.id ? "help-toc-link--active" : ""
                                    }`}
                                    onClick={() => setActiveId(s.id)}
                                >
                                    {Icon ? <Icon className="w-3.5 h-3.5" /> : null}
                                    <span className="flex-1">{s.title}</span>
                                    {activeId === s.id ? (
                                        <Check className="w-3 h-3 text-ux-accent" />
                                    ) : null}
                                </a>
                            );
                        })}
                        {filtered.length === 0 ? (
                            <div className="px-3 py-2 text-[11px] text-ux-text-3 font-mono">
                                No sections match "{query}"
                            </div>
                        ) : null}
                    </nav>
                    <div className="help-toc-footer">
                        <Link
                            to="/docs"
                            className="inline-flex items-center gap-2 text-[11px] font-mono text-ux-text-3 hover:text-ux-accent"
                            data-testid="help-public-docs-link"
                        >
                            <BookOpen className="w-3 h-3" /> Public docs
                        </Link>
                        <Link
                            to="/plan/scenarios"
                            className="inline-flex items-center gap-2 text-[11px] font-mono text-ux-text-3 hover:text-ux-accent"
                            data-testid="help-back-console"
                        >
                            <LineChart className="w-3 h-3" /> Back to console
                        </Link>
                    </div>
                </aside>

                {/* ── Long-form body ─────────────────────────────────── */}
                <main id="help-main" className="help-main" data-testid="help-main">
                    {visibleSections.map((s) => {
                        const Icon = s.icon;
                        return (
                            <section
                                key={s.id}
                                id={s.id}
                                ref={(el) => {
                                    if (el) sectionsRef.current[s.id] = el;
                                }}
                                data-testid={`help-section-${s.id}`}
                                className="help-section"
                            >
                                <h2 className="help-section-title">
                                    {Icon ? <Icon className="w-5 h-5 text-ux-accent" /> : null}
                                    {s.title}
                                </h2>
                                <div className="help-section-body">{s.body()}</div>
                            </section>
                        );
                    })}
                    <footer className="help-footer">
                        <Lock className="w-3.5 h-3.5 text-ux-text-3" />
                        <span>
                            Internal documentation · authenticated access only ·
                            do not mirror externally.
                        </span>
                    </footer>
                </main>
            </div>
        </div>
    );
}
